import React, { useState, useMemo, useEffect } from "react";
import { Input, Tree, Button, Typography, Flex, Spin } from "antd";
import {
  SearchOutlined,
  ArrowLeftOutlined,
  DownOutlined,
  RightOutlined,
} from "@ant-design/icons";
// eslint-disable-next-line no-duplicate-imports
import type { TreeDataNode, TreeProps } from "antd";
import AxiosInstance from "../../../shared/utils/axios";
import "./TrialTreeStyles.less";
import { formatTranslation } from "../../../shared/utils/translationUtils";
import { t } from "i18next";

const { Text, Title } = Typography;
export interface CreateMultipleReportProps {
  back: () => void;
  generateReport: (data: {
    field_ids: string[];
    trial_ids: string[];
  }) => Promise<void>;
  propertyId: string;
  checkedKeys: React.Key[];
  setCheckedKeys: React.Dispatch<React.SetStateAction<React.Key[]>>;
}
interface TreeNode extends TreeDataNode {
  key: string;
  title: string;
  children?: TreeNode[];
  style?: React.CSSProperties;
  isTrial?: boolean;
  isField?: boolean;
  trialCount?: number;
  trialTypes?: string[];
}
interface ApiFieldData {
  id: string;
  name: string;
  declared_area: number;
  calculated_area: number;
  trials: Array<{
    id: string;
    name: string;
    field_ids: string[];
    trial_types: string[];
  }>;
}
type ApiResponse = Record<string, ApiFieldData>;
function getFieldIdsByTrialIds(
  data: ApiResponse,
  trialIds: string[]
): string[] {
  const fieldIdSet = new Set<string>();
  const fields = Object.values(data ?? {});
  fields.forEach((field) => {
    field.trials.forEach((trial) => {
      if (trialIds.includes(trial.id)) {
        trial.field_ids.forEach((fieldId) => {
          fieldIdSet.add(fieldId);
        });
      }
    });
  });
  return Array.from(fieldIdSet);
}
export const TrialTreeComponent: React.FC<CreateMultipleReportProps> = ({
  back,
  generateReport,
  propertyId,
  checkedKeys,
  setCheckedKeys,
}) => {
  const [searchValue, setSearchValue] = useState("");
  const [isGenerating, setIsGenerating] = useState(false);
  const [expandedKeys, setExpandedKeys] = useState<React.Key[]>([
    "all-trials",
    "trial-2025",
  ]);
  const [treeData, setTreeData] = useState<TreeNode[]>([]);
  const [loading, setLoading] = useState(false);
  const [apiResponse, setApiResponse] = useState<ApiResponse>({});
  const formatApiDataToTree = (apiData: ApiResponse): TreeNode[] => {
    let totalTrialCount = 0;
    const allTrialsNode: TreeNode = {
      key: "all-trials",
      title: "All trials",
      children: [],
      trialCount: 0,
    };

    Object.values(apiData).forEach((fieldData) => {
      const trialCount = fieldData.trials.length;
      totalTrialCount += trialCount;
      const fieldNode: TreeNode = {
        key: `field-${fieldData.id}`,
        title: fieldData.name,
        isField: true,
        children: [],
        trialCount,
      };
      fieldData.trials.forEach((trial) => {
        const trialNode: TreeNode = {
          key: `trial-${trial.id}`,
          title: trial.name,
          style: {
            fontWeight: "600",
            color: "var(--Neutral-neutral-100, #14151C)",
            paddingLeft: "8px",
          },
          isTrial: true,
          trialTypes: trial.trial_types,
        };
        fieldNode.children?.push(trialNode);
      });
      allTrialsNode.children?.push(fieldNode);
    });
    // allTrialsNode.title = `All trials (${totalTrials})`;
    allTrialsNode.trialCount = totalTrialCount;
    return [allTrialsNode];
  };
  const fetchTrialData = async (): Promise<void> => {
    setLoading(true);
    try {
      const response = await AxiosInstance.get(
        `/property/${propertyId}/trials`
      );
      const data: ApiResponse = await response.data;
      setApiResponse(data);
      const formattedData = formatApiDataToTree(data);
      setTreeData(formattedData);
    } catch (error) {
      console.error("Error fetching trial data:", error);
    } finally {
      setLoading(false);
    }
  };
  useEffect(() => {
    void fetchTrialData();
  }, [propertyId]);
  const filteredData = useMemo(() => {
    if (!searchValue.trim()) return treeData;
    const matchedKeys = new Set<string>();
    const searchNodes = (nodes: TreeNode[]): TreeNode[] => {
      return nodes
        .filter((node) => {
          const matches = node.title
            .toLowerCase()
            .includes(searchValue.toLowerCase());
          if (matches) matchedKeys.add(node.key);
          if (node.children) {
            node.children = searchNodes(node.children);
            return node.children.length > 0 || matches;
          }
          return matches;
        })
        .map((node) => ({ ...node }));
    };
    const result = searchNodes(JSON.parse(JSON.stringify(treeData)));
    setExpandedKeys((prev) => [
      ...new Set([...prev, ...Array.from(matchedKeys)]),
    ]);
    return result;
  }, [searchValue, treeData]);
  const onCheck: TreeProps["onCheck"] = (checkedKeys) => {
    setCheckedKeys(checkedKeys as React.Key[]);
  };

  const handleGenerateReport = async (): Promise<void> => {
    const fieldIds: string[] = [];
    const trialIds: string[] = [];
    checkedKeys.forEach((key) => {
      const keyStr = key.toString();
      if (keyStr.startsWith("field-")) {
        fieldIds.push(keyStr.replace("field-", ""));
      } else if (
        keyStr.startsWith("trial-") ||
        keyStr.startsWith("trial-2025-")
      ) {
        trialIds.push(keyStr.replace("trial-", "").replace("trial-2025-", ""));
      }
    });

    setIsGenerating(true);
    try {
      await generateReport({
        field_ids: getFieldIdsByTrialIds(apiResponse, trialIds),
        trial_ids: trialIds,
      });
    } finally {
      setIsGenerating(false);
    }
  };

  return (
    <div className="trial-tree-container">
      <div style={{ position: "relative", flex: 1 }}>
        {isGenerating && (
          <div className="report-generating-overlay">
            <Spin size="large" />
            <div className="generating-text">
              <Title level={5}>
                {formatTranslation(t("report.multi.report"))}
              </Title>
              <Text type="secondary">
                {formatTranslation(t("report.multi.generating"))}
              </Text>
            </div>
          </div>
        )}
      </div>
      <div
        style={{
          display: "flex",
          flexDirection: "column",
          height: "100%",
          gap: 16,
          padding: "10px 24px",
        }}
      >
        <Input
          placeholder="Search for fields or reports"
          prefix={<SearchOutlined />}
          value={searchValue}
          onChange={(e) => {
            setSearchValue(e.target.value);
          }}
          allowClear
          style={{ marginBottom: 8 }}
          className="search-input"
        />
        <div
          style={{ flex: 1, overflow: "auto", maxHeight: "60vh" }}
          className="tree-wrapper"
        >
          {loading ? (
            <Spin tip="Loading trial data..." />
          ) : (
            <Tree
              className="custom-tree"
              checkable
              showLine={{ showLeafIcon: false }}
              expandedKeys={expandedKeys}
              onExpand={setExpandedKeys}
              onCheck={onCheck}
              checkedKeys={checkedKeys}
              treeData={filteredData}
              blockNode
              selectable={false}
              style={{ fontSize: 14, lineHeight: "32px" }}
              switcherIcon={(props: any) => {
                // eslint-disable-next-line react/prop-types
                const { isLeaf, expanded } = props;
                if (isLeaf) return null;
                return expanded ? <DownOutlined /> : <RightOutlined />;
              }}
              titleRender={(node) => {
                // Function to highlight search matches
                const highlightSearch = (text: string): React.ReactNode => {
                  if (!searchValue.trim()) return text;
                  const regex = new RegExp(`(${searchValue})`, "gi");
                  return text.split(regex).map((part, i) =>
                    part.toLowerCase() === searchValue.toLowerCase() ? (
                      <span key={i} className="search-highlight">
                        {part}
                      </span>
                    ) : (
                      part
                    )
                  );
                };
                return (
                  <div className="trial-node-container">
                    <div className="trial-name-line">
                      <Text style={node.style}>
                        {highlightSearch(node.title)}
                      </Text>
                      {typeof node.trialCount === "number" && (
                        <span className="trial-count-badge">
                          {node.trialCount}
                        </span>
                      )}
                    </div>

                    {node.isTrial && node.trialTypes && (
                      <div
                        style={{
                          marginTop: 0,
                          display: "flex",
                          flexWrap: "wrap",
                          gap: 4,
                        }}
                      >
                        {node.trialTypes.map((type, index) => (
                          <span
                            key={index}
                            style={{
                              fontFamily: "Noto Sans",
                              fontWeight: 400,
                              color: "#696F88",
                              padding: "0px 8px",
                              borderRadius: "4px",
                              fontSize: "12px",
                            }}
                          >
                            {type}
                          </span>
                        ))}
                      </div>
                    )}
                  </div>
                );
              }}
            />
          )}
        </div>
        <Flex
          justify="space-between"
          className="action-buttons"
          style={{ marginTop: 16 }}
        >
          <Button icon={<ArrowLeftOutlined />} onClick={back}>
            {formatTranslation(t("report.trial.back"))}
          </Button>
          <Button
            className="generate-report-button"
            onClick={() => {
              void handleGenerateReport();
            }}
            disabled={!checkedKeys.length || loading}
            loading={loading}
          >
            {formatTranslation(t("report.trial.btn"))}
          </Button>
        </Flex>
      </div>
    </div>
  );
};
export default TrialTreeComponent;
